import React, { useState, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Fab,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  ManageAccounts as ManageMembersIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useGroups } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import GroupForm from '../components/GroupForm';
import GroupCard from '../components/GroupCard';
import GroupDiagnostics from '../components/GroupDiagnostics';
import PageContainer from '../components/PageContainer';

export default function GroupsPage() {
  const { groups, loading, error, refreshGroups } = useGroups();
  const { currentUser } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterPrivacy, setFilterPrivacy] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState(0);

  const handleCreateGroup = () => setCreateDialogOpen(true);
  const handleCloseCreateDialog = () => setCreateDialogOpen(false);

  // Filter and search groups
  const filteredAndSortedGroups = useMemo(() => {
    let filtered = groups.filter(group => {
      const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' ||
        (filterRole === 'admin' && group.createdBy === currentUser?.uid) ||
        (filterRole === 'member' && group.createdBy !== currentUser?.uid);
      const matchesPrivacy = filterPrivacy === 'all' || group.privacy === filterPrivacy;
      return matchesSearch && matchesRole && matchesPrivacy;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'members':
          return (b.memberCount || 1) - (a.memberCount || 1);
        case 'recent':
        default:
          const dateA = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
      }
    });

    return filtered;
  }, [groups, searchTerm, filterRole, filterPrivacy, sortBy, currentUser]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Loading your groups...</Typography>
          </Box>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            My Groups
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Quick access to your groups. Use search and filters to find what you need.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={refreshGroups}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <GroupIcon />}
          size="small"
        >
          {loading ? 'Refreshing...' : 'Refresh Groups'}
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            sx={{ minWidth: { xs: '100%', md: 300 } }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select value={filterRole} label="Role" onChange={(e) => setFilterRole(e.target.value)}>
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Privacy</InputLabel>
            <Select value={filterPrivacy} label="Privacy" onChange={(e) => setFilterPrivacy(e.target.value)}>
              <MenuItem value="all">All Privacy</MenuItem>
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="friends">Friends</MenuItem>
              <MenuItem value="public">Public</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="recent">Most Recent</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="members">Most Members</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Tooltip title="Grid View">
              <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'} size="small">
                <DashboardIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="List View">
              <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'} size="small">
                <ViewListIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setShowDiagnostics(!showDiagnostics)} variant="outlined">
              {showDiagnostics ? 'Hide' : 'Show'} Diagnostics
            </Button>
          </Box>
        </Alert>
      )}

      {showDiagnostics && <GroupDiagnostics />}

      {/* Groups Content */}
      {filteredAndSortedGroups.length > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="group views">
              <Tab label={`All (${filteredAndSortedGroups.length})`} />
              <Tab label={`Admin (${filteredAndSortedGroups.filter(g => g.createdBy === currentUser?.uid).length})`} />
              <Tab label={`Member (${filteredAndSortedGroups.filter(g => g.createdBy !== currentUser?.uid).length})`} />
            </Tabs>
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 0}>
            {activeTab === 0 && (
              <GroupsGridView groups={filteredAndSortedGroups} viewMode={viewMode} />
            )}
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 1}>
            {activeTab === 1 && (
              <GroupsGridView groups={filteredAndSortedGroups.filter(g => g.createdBy === currentUser?.uid)} viewMode={viewMode} />
            )}
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 2}>
            {activeTab === 2 && (
              <GroupsGridView groups={filteredAndSortedGroups.filter(g => g.createdBy !== currentUser?.uid)} viewMode={viewMode} />
            )}
          </Box>
        </>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Groups Found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Try adjusting your search or filters to find groups.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateGroup} size="large">
            Create Group
          </Button>
        </Paper>
      )}

      {/* Create Group FAB */}
      <Fab color="primary" aria-label="create group" onClick={handleCreateGroup} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
        <AddIcon />
      </Fab>

      {/* Create Group Dialog */}
      <GroupForm open={createDialogOpen} onClose={handleCloseCreateDialog} mode="create" />
    </PageContainer>
  );
}

function GroupsGridView({ groups, viewMode }) {
  if (viewMode === 'list') {
    return (
      <Stack spacing={2}>
        {groups.map((group) => (
          <GroupListCard key={group.id} group={group} />
        ))}
      </Stack>
    );
  }

  return (
    <Grid container spacing={3}>
      {groups.map((group) => (
        <Grid item xs={12} sm={6} md={4} key={group.id}>
          <GroupCard group={group} />
        </Grid>
      ))}
    </Grid>
  );
}

function GroupListCard({ group }) {
  const { isUserCreator, isUserAdmin } = useGroups();
  const isCreator = isUserCreator(group.id);
  const isAdmin = isUserAdmin(group.id);

  const getRoleIcon = () => (isCreator ? <AdminIcon color="primary" /> : isAdmin ? <AdminIcon color="secondary" /> : <PersonIcon />);
  const getRoleColor = () => (isCreator ? 'primary' : isAdmin ? 'secondary' : 'default');
  const getRoleLabel = () => (isCreator ? 'Creator' : isAdmin ? 'Admin' : 'Member');

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupIcon color="primary" />
          <Box>
            <Typography variant="h6" component="h3">{group.name}</Typography>
            <Typography variant="body2" color="text.secondary">{group.description || 'No description'}</Typography>
          </Box>
        </Box>
        <Chip icon={getRoleIcon()} label={getRoleLabel()} size="small" color={getRoleColor()} variant="outlined" />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">{group.memberCount || 1} members</Typography>
        <Typography variant="body2" color="text.secondary">Created {group.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<EventIcon />}>Create Party</Button>
        <Button size="small" variant="outlined" startIcon={<ManageMembersIcon />}>Members</Button>
        <Button size="small" variant="outlined" startIcon={<SettingsIcon />}>Settings</Button>
      </Box>
    </Paper>
  );
}
